import React, {
  useState,
  useEffect,
  useContext,
  useRef,
} from 'react';
import { connect } from 'react-redux';
import { NAVBAR_THEME } from 'lib/navbar';
import BTE from 'lib/BTE';
import { setTheme as setThemeAction } from 'redux/modules/navbar';
import { LayoutContext } from 'lib/ContextTypes';

const transitionTheme = NAVBAR_THEME.LIGHT;

/**
 * Maps dispatch actions to component props.
 * @param {Function} dispatch - The Redux dispatch function.
 * @returns {object} An object containing action dispatchers.
 */
const mapActionsToProps = (dispatch) => ({
  /**
   * Dispatches an action to set the theme.
   * @param {string} theme - The theme to set.
   */
  setTheme: (theme) => dispatch(setThemeAction(theme)),
});

/**
 * Maps state from the Redux store to component props.
 * @param {object} state - The Redux state.
 * @param {object} state.layout - The layout state.
 * @param {object} state.navbar - The navbar state.
 * @returns {object} An object containing mapped state properties.
 */
const mapStateToProps = ({
  layout,
  navbar,
}) => ({
  triggerTop: layout.triggerTop,
  navTheme: navbar.theme,
});

/**
 * Wrapper component for managing layout and theme transitions.
 * @param {object} props - The component props.
 * @param {string} props.id - The unique identifier for the wrapper.
 * @param {string} props.className - Additional CSS classes for the wrapper.
 * @param {React.ReactNode} props.children - The child components to render inside the wrapper.
 * @param {Function} props.setTheme - Function to set the navbar theme.
 * @param {string} props.navTheme - The current navbar theme.
 * @param {number} [props.packageIndex=0] - The index of the package in the layout.
 * @param {number} [props.triggerTop=0] - The scroll depth to trigger theme change.
 * @returns {JSX.Element} The rendered wrapper component.
 */
const Wrapper = ({
  id,
  className,
  children,
  setTheme,
  navTheme,
  packageIndex = 0,
  triggerTop = 0,
}) => {
  const { layoutIndex } = useContext(LayoutContext);

  const [originalTheme] = useState(navTheme); // Store original theme value for transition
  const containerRef = useRef(null);

  /**
   * Handles scroll events to determine if the navbar theme should change.
   * @param {number} depth - The current scroll depth.
   */
  const handleScroll = (depth) => {
    if (depth > triggerTop && navTheme !== transitionTheme) {
      setTheme(transitionTheme);
      return;
    }

    if (depth <= triggerTop && navTheme !== originalTheme) {
      setTheme(originalTheme);
    }
  };

  useEffect(() => {
    // Only monitor scrolling on the first package of layout and a theme transition has been configured
    if (layoutIndex === 0 && packageIndex === 0 && originalTheme !== transitionTheme) {
      BTE.on('scroll', handleScroll, 'throttle');

      if (window.scrollY > triggerTop && navTheme !== transitionTheme) {
        setTheme(transitionTheme);
      }
    }

    return () => {
      if (layoutIndex === 0 && packageIndex === 0) {
        BTE.remove('scroll', handleScroll);
      }
    };
  }, [layoutIndex, packageIndex, originalTheme, navTheme, setTheme, triggerTop]);

  return (
    <div
      data-packageid={id}
      className={className}
      ref={containerRef}
      data-testid="header-package-wrapper"
    >
      {children}
    </div>
  );
};
// Disabled this lint rule to minimize impact during class component upgrade as this component is widely used.
// eslint-disable-next-line import/no-default-export
export default connect(mapStateToProps, mapActionsToProps)(Wrapper);
